/**
 * Profile screen — the current user's own profile (the `Profile` bottom tab).
 *
 * MIGRATION (bare RN, P3-6): ported off Expo —
 *   expo-router          -> @react-navigation (useNavigation + useFocusEffect
 *                           from @react-navigation/native; route names per
 *                           navigation/types.ts).
 *   @expo/vector-icons   -> components/ui/Icon shim.
 *   expo-linear-gradient -> react-native-linear-gradient.
 * Services (api) and the optimistic like/repost logic are verbatim.
 *
 * APPLE-ALIGNED TREATMENT (matches Home/Search/Notifications/DM/Communities):
 *   - Full-bleed <ParticleBackground/> aurora as the absoluteFill first child;
 *     the screen background is transparent so the aurora shows through (skill §7).
 *   - A translucent in-screen header (GlassSurface bar) that the list scrolls
 *     UNDER — no opaque top strip, no gap (skill §12). The native header stays
 *     hidden (the Tabs navigator already hides it; we self-manage for safety).
 *   - x.com feel: banner + overlapping avatar, a prominent glass "Edit Profile"
 *     affordance, glass segmented tabs and a CENTERED media/gallery grid tab.
 *   - Type per skill §15 (negative tracking on the large display name).
 *
 * Data flow (fetchProfile):
 *   1. GET /auth/me              → identity (id, username, avatar) but NO counts.
 *   2. GET /users/profile/:user  → display name, bio, follower/post/following counts.
 *   3. GET /users/:user/posts    → the user's posts for the Posts / Media tabs.
 *   4. GET /users/me/communities → joined communities for the Communities tab.
 * Steps 2-4 run in parallel after step 1 resolves the username.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Share,
  StatusBar,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import type { CompositeNavigationProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import RichPostText from '../components/feed/RichPostText';
import Asset3D from '../components/ui/Asset3D';
import { navigate as navRef } from '../navigation/navigationRef';
import LinearGradient from 'react-native-linear-gradient';
import { colors, spacing, radius, fonts, typographyScale, shadowTokens, getLayout, layout as layoutTokens } from '../constants/theme';
import { GlassSurface, GlassButton, GlassPill, glassTokens } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import { api } from '../services/api';
import { seedEngagement, subscribe as subscribeEngagement, applyLike, applyRepost, getEngagement } from '../services/postEngagement';
import { subscribeCounts } from '../services/socialGraph';
import { friendlyDisplayName } from '../lib/identity';
import { tabularNums } from '../lib/format';
import { displayableIcon } from '../lib/icons';
import ScreenTip from '../components/ui/ScreenTip';
import type { RootStackParamList, TabsParamList } from '../navigation/types';

// Profile lives in the Tabs navigator but pushes onto the RootStack, so its
// navigation prop is the composite of both.
type Nav = CompositeNavigationProp<
  BottomTabNavigationProp<TabsParamList, 'Profile'>,
  NativeStackNavigationProp<RootStackParamList>
>;

// Tabs shown under the profile header. Media is the x.com-style centered gallery
// grid of the user's own posts; the old Replies/Likes tabs were inert stubs.
const PROFILE_TABS: { key: string; label: string; icon: any }[] = [
  { key: 'Posts', label: 'Posts', icon: 'document-text-outline' },
  { key: 'Media', label: 'Media', icon: 'grid-outline' },
  { key: 'Communities', label: 'Communities', icon: 'people-outline' },
];

interface User {
  id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  banner_url?: string;
  posts_count: number;
  followers_count: number;
  following_count: number;
}

interface Post {
  id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  reposts_count: number;
  liked: boolean;
  reposted: boolean;
}

// Backend user post shape: { id, content, createdAt, repostCount, liked,
// reposted, User, _count: { Vote, Comment, Award } }. Normalize to `Post`.
function normalizePost(raw: any): Post {
  return {
    id: raw?.id,
    content: raw?.content ?? '',
    created_at: raw?.createdAt ?? raw?.created_at ?? '',
    likes_count: raw?._count?.Vote ?? raw?.upvotes ?? 0,
    comments_count: raw?._count?.Comment ?? raw?.commentCount ?? 0,
    reposts_count: raw?.repostCount ?? 0,
    liked: raw?.liked ?? false,
    reposted: raw?.reposted ?? false,
  };
}

function timeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const ts = new Date(dateStr).getTime();
  if (isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

// Abbreviates large stat counts (1.2K, 3.4M) for the followers/posts row.
function formatCount(n?: number): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(v);
}

// Compact post card for the profile list. Like/repost route through the shared
// postEngagement store (optimistic toggle + real DELETE/POST, rollback on
// failure) so a like made here reflects on the same post in the feed / detail
// without a refetch — same wiring as home.tsx / post/[id] / UserProfileScreen.
function ProfilePostCard({ post, onPress }: { post: Post; onPress: () => void }) {
  const [eng, setEng] = useState(() =>
    getEngagement(post.id) ?? seedEngagement(post.id, {
      liked: post.liked ?? false, likes: post.likes_count ?? 0,
      reposted: post.reposted ?? false, reposts: post.reposts_count ?? 0, bookmarked: false,
    })
  );
  useEffect(() => {
    seedEngagement(post.id, {
      liked: post.liked ?? false, likes: post.likes_count ?? 0,
      reposted: post.reposted ?? false, reposts: post.reposts_count ?? 0,
    });
    return subscribeEngagement(post.id, setEng);
  }, [post.id]);

  const toggleLike = () => {
    const was = eng.liked;
    applyLike(post.id, !was); // optimistic + notifies subscribers
    api(`/posts/${post.id}/like`, { method: was ? 'DELETE' : 'POST' }).then((r) => { if (!r.success) applyLike(post.id, was); });
  };
  const toggleRepost = () => {
    const was = eng.reposted;
    applyRepost(post.id, !was);
    api(`/posts/${post.id}/repost`, { method: was ? 'DELETE' : 'POST' }).then((r) => { if (!r.success) applyRepost(post.id, was); });
  };

  return (
    <GlassSurface
      variant="card"
      radius={glassTokens.radius.md}
      onPress={onPress}
      style={styles.postCard}
      contentStyle={styles.postCardContent}
      accessibilityLabel="Open post"
    >
      <RichPostText content={post.content || ''} style={styles.postContent} numberOfLines={8} onCashtag={(symbol) => navRef('CoinDetail', { symbol })} />
      <Text style={styles.postTime}>{timeAgo(post.created_at)}</Text>
      <View style={styles.postActions}>
        <TouchableOpacity
          style={styles.postAction}
          onPress={toggleLike}
          accessibilityRole="button"
          accessibilityLabel={eng.liked ? 'Unlike post' : 'Like post'}
        >
          <Ionicons name={eng.liked ? 'heart' : 'heart-outline'} size={16} color={eng.liked ? colors.error : colors.muted} />
          <Text style={styles.postActionCount}>{eng.likes}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.postAction}
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel="View comments"
        >
          <Ionicons name="chatbubble-outline" size={15} color={colors.muted} />
          <Text style={styles.postActionCount}>{post.comments_count || 0}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.postAction}
          onPress={toggleRepost}
          accessibilityRole="button"
          accessibilityLabel={eng.reposted ? 'Undo repost' : 'Repost'}
        >
          <Ionicons name="repeat" size={17} color={eng.reposted ? colors.success : colors.muted} />
          <Text style={styles.postActionCount}>{eng.reposts}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.postAction}
          onPress={() => { Share.share({ message: `https://platform.cryb.ai/post/${post.id}` }); }}
          accessibilityRole="button"
          accessibilityLabel="Share post"
        >
          <Ionicons name="share-outline" size={15} color={colors.muted} />
        </TouchableOpacity>
      </View>
    </GlassSurface>
  );
}

// A single square tile in the centered Media gallery grid. Shows a preview of the
// post (its opening line lifted onto a translucent brand tile) so the grid reads
// as a real gallery even before rich media attachments ship. Tap opens the post.
function MediaTile({ post, size, onPress }: { post: Post; size: number; onPress: () => void }) {
  const preview = (post.content || '').trim().split('\n')[0] || 'Post';
  return (
    <TouchableOpacity
      style={[styles.mediaTile, { width: size, height: size }]}
      onPress={onPress}
      activeOpacity={0.85}
      accessibilityRole="imagebutton"
      accessibilityLabel="Open post"
    >
      <LinearGradient
        colors={[colors.primary, colors.primaryLight]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <LinearGradient
        colors={['transparent', 'rgba(0,0,0,0.35)']}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <Text style={styles.mediaTileText} numberOfLines={4}>{preview}</Text>
      <View style={styles.mediaTileMeta}>
        <Ionicons name="heart" size={11} color="rgba(255,255,255,0.9)" />
        <Text style={styles.mediaTileMetaText}>{formatCount(post.likes_count)}</Text>
      </View>
    </TouchableOpacity>
  );
}

export default function ProfileScreen() {
  // ── Layout / navigation ──
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  // Constrain width on larger viewports so the profile stays a centered column.
  const columnMax = layout === 'phone' ? width : layout === 'desktop' ? 720 : 620;
  const containerStyle = layout !== 'phone' ? { maxWidth: columnMax, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  // Self-manage the native header (skill §12) — the Tabs navigator already hides
  // it, but keep this explicit so the translucent in-screen header is the only
  // top chrome and nothing can reintroduce an opaque bar.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // ── State ──
  const [user, setUser] = useState<User | null>(null);
  // Mirror of `user` for reads inside the stable fetchProfile (below) — lets that
  // callback check "do we have a user to show?" without listing `user` in its deps
  // (which re-created it on every load → redundant mount + focus refetches).
  const userRef = useRef<User | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinguish a transient load FAILURE (retryable) from a genuinely-empty
  // profile — was showing a fake "@user / 0 Posts" shell on any network error.
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('Posts');

  // ── Data fetching ──
  // Resolves identity via /auth/me, then hydrates counts and posts in parallel.
  const fetchProfile = useCallback(async () => {
    setError(false);
    try {
      // /auth/me wraps as { data: { user: {...camelCase} } } — api() unwraps one
      // level so meRes.data is { user }. It has no follower/post counts, so pull
      // those from /users/profile/:username.
      const meRes = await api<{ user?: any }>('/auth/me');
      const me = meRes.success ? meRes.data?.user ?? meRes.data : null;
      if (me && me.username) {
        const username = me.username;
        // Map camelCase backend user -> client snake_case User shape.
        // PRESERVE the last known counts (prev) instead of resetting to 0: this
        // runs on every screen focus (useFocusEffect), and zeroing here made the
        // stat row flash "0" then jump back to the real value — the "number
        // glitching". Counts stay put until the fresh /profile fetch updates them.
        setUser((prev) => ({
          id: me.id,
          username,
          display_name: me.displayName ?? me.display_name ?? prev?.display_name ?? '',
          bio: me.bio ?? prev?.bio,
          avatar_url: me.avatar ?? me.avatar_url ?? prev?.avatar_url,
          banner_url: me.banner ?? me.banner_url ?? prev?.banner_url,
          posts_count: prev?.posts_count ?? 0,
          followers_count: prev?.followers_count ?? 0,
          following_count: prev?.following_count ?? 0,
        }));

        // Fetch full profile (counts) + posts in parallel.
        const [profileRes, postsRes, communitiesRes] = await Promise.all([
          api<any>(`/users/profile/${encodeURIComponent(username)}`),
          api<{ items?: any[] }>(`/users/${encodeURIComponent(username)}/posts`),
          api<any>(`/users/me/communities`),
        ]);

        if (communitiesRes.success && communitiesRes.data) {
          const d: any = communitiesRes.data;
          const list = d.items ?? d.communities ?? (Array.isArray(d) ? d : []);
          setCommunities(Array.isArray(list) ? list : []);
        }

        if (profileRes.success && profileRes.data) {
          const p = profileRes.data;
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  display_name: p.displayName ?? prev.display_name,
                  bio: p.bio ?? prev.bio,
                  avatar_url: p.avatar ?? prev.avatar_url,
                  posts_count: p.postsCount ?? 0,
                  followers_count: p.followersCount ?? 0,
                  following_count: p.followingCount ?? 0,
                }
              : prev
          );
        }

        if (postsRes.success && postsRes.data) {
          // Read whichever envelope the API uses (items | posts | bare array) so
          // posts render regardless of shape (empty-but-200 guard).
          const arr = Array.isArray(postsRes.data?.items) ? postsRes.data.items
            : Array.isArray((postsRes.data as any)?.posts) ? (postsRes.data as any).posts
            : Array.isArray(postsRes.data) ? postsRes.data : [];
          setPosts(arr.map(normalizePost));
        }
      }
      // /auth/me failed for a reason OTHER than an expired session (the auth layer
      // routes expired tokens to login). If we have no user to show, surface a
      // retryable error instead of a fake empty shell.
      if (!meRes.success && !userRef.current) setError(true);
    } catch {
      // Network/parse failure with nothing to show → retryable error state.
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchProfile();
    setRefreshing(false);
  }, [fetchProfile]);

  // ── Effects ──
  // Load the profile once on mount.
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Refresh on focus so a newly created post / joined community appears when you
  // return here (e.g. from the composer). Skip the initial focus — mount loads it.
  const didInitialFocus = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (!didInitialFocus.current) { didInitialFocus.current = true; return; }
      fetchProfile();
    }, [fetchProfile])
  );

  // LR4: subscribe to live count changes for OUR OWN profile. Following someone
  // from another screen reconciles the actor's following-count in the shared
  // socialGraph store (setFollow → applyCounts on the actor) and follow:update
  // socket events patch it too. Without this, our Following/Followers counts only
  // corrected on the next focus refetch (stale in the meantime). Patch from the
  // AUTHORITATIVE record; the focus-refetch above still runs unchanged.
  useEffect(() => {
    if (!user?.id && !user?.username) return;
    const unsub = subscribeCounts({ id: user.id, username: user.username }, (c) => {
      setUser((prev) => {
        if (!prev) return prev;
        if (prev.followers_count === c.followerCount && prev.following_count === c.followingCount) return prev;
        return { ...prev, followers_count: c.followerCount, following_count: c.followingCount };
      });
    });
    return unsub;
  }, [user?.id, user?.username]);

  // Centered media grid geometry: 3 columns within the (constrained) column.
  const MEDIA_COLS = 3;
  const mediaGutter = spacing.xs;
  const mediaTileSize = useMemo(() => {
    const inner = Math.min(width, columnMax) - spacing.lg * 2;
    return Math.floor((inner - mediaGutter * (MEDIA_COLS - 1)) / MEDIA_COLS);
  }, [width, columnMax, mediaGutter]);

  // Transparent floating top header (client: "el top tiene que verse uniforme /
  // transparente"). No opaque glass strip — the content scrolls UNDER a soft
  // top scrim so the banner/aurora reads as one continuous surface. The icons
  // float over that scrim; only a faint gradient keeps them legible (skill §12).
  // Absolutely positioned so it never introduces an in-flow opaque break; the
  // list carries matching top padding so nothing is trapped behind the icons.
  const TopHeader = () => (
    <View style={styles.topHeader} pointerEvents="box-none">
      {/* Soft DARK scrim — the banner now bleeds to the very top, so a gentle
          top-down darken keeps the glassy white icons + title legible over the
          purple (and over lighter content once scrolled). */}
      <LinearGradient
        colors={['rgba(0,0,0,0.30)', 'rgba(0,0,0,0.10)', 'transparent']}
        locations={[0, 0.6, 1]}
        style={[styles.topHeaderScrim, { height: insets.top + TOP_HEADER_ROW }]}
        pointerEvents="none"
      />
      <View style={[styles.topHeaderContent, { paddingTop: insets.top + spacing.xs }]} pointerEvents="box-none">
        <Text style={styles.topHeaderTitle} numberOfLines={1} accessibilityRole="header">Profile</Text>
        <View style={styles.topHeaderActions}>
          <TouchableOpacity
            style={styles.topHeaderBtn}
            onPress={() => { if (user?.username) Share.share({ message: `https://platform.cryb.ai/u/${user.username}` }); }}
            accessibilityRole="button"
            accessibilityLabel="Share profile"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="share-outline" size={19} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.topHeaderBtn}
            onPress={() => navigation.navigate('Settings', { screen: 'SettingsIndex' })}
            accessibilityRole="button"
            accessibilityLabel="Settings"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="settings-outline" size={19} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  // ── Render ──
  // While the first fetch is in flight: keep the aurora + the translucent header
  // in place (no chrome jump) and center a spinner in the body below it.
  if (loading) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="dark-content" />
        <ParticleBackground theme="light" count={5} />
        <TopHeader />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  // Retryable load-failure state (was rendering a fake "@user / 0 Posts" shell).
  if (error && !user) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="dark-content" />
        <ParticleBackground theme="light" count={5} />
        <TopHeader />
        <View style={styles.loadingWrap}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={{ ...typographyScale.body, color: colors.muted, textAlign: 'center', marginTop: spacing.md, marginBottom: spacing.lg }}>
            Couldn't load your profile. Check your connection.
          </Text>
          <GlassButton title="Retry" variant="secondary" onPress={() => { setLoading(true); fetchProfile(); }} />
        </View>
      </View>
    );
  }

  const listData = activeTab === 'Communities'
    ? (Array.isArray(communities) ? communities : [])
    : (Array.isArray(posts) ? posts : []);

  // Header above the list: banner + overlapping avatar, identity, stats, actions,
  // and the glass segmented tabs (x.com layout).
  const ProfileHeader = () => (
    <View>
      {/* Banner + overlapping avatar. The banner BLEEDS to the very top (behind
          the status bar + floating glass icons) and grows by the top inset so the
          avatar/name still sit clear of the notch. */}
      <View style={[styles.bannerWrap, { height: BANNER_HEIGHT + insets.top + AVATAR_SIZE / 2 }]}>
        {user?.banner_url && user.banner_url.length > 0 ? (
          <CachedImage source={{ uri: user.banner_url }} style={[styles.banner, { height: BANNER_HEIGHT + insets.top }]} />
        ) : (
          <LinearGradient
            colors={[colors.primary, colors.primaryLight]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.banner, { height: BANNER_HEIGHT + insets.top }]}
          />
        )}
        {/* Bottom scrim so the avatar + name lift cleanly off the banner. */}
        <LinearGradient
          colors={['transparent', 'rgba(0,0,0,0.22)']}
          style={[styles.bannerScrim, { top: BANNER_HEIGHT + insets.top - 56 }]}
          pointerEvents="none"
        />
        {/* Avatar with an "edit" affordance overlay (x.com editable feel). */}
        <TouchableOpacity
          style={styles.avatarWrap}
          onPress={() => navigation.navigate('EditProfile')}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel="Edit profile photo"
        >
          {displayableIcon(user?.avatar_url) ? (
            <CachedImage source={{ uri: displayableIcon(user?.avatar_url)! }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Text style={styles.avatarLetter}>
                {(user?.display_name || user?.username || 'U').charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <View style={styles.avatarEditBadge}>
            <Ionicons name="camera" size={13} color="#FFFFFF" />
          </View>
        </TouchableOpacity>

        {/* Edit Profile — floats at the banner's trailing edge, mirroring x.com. */}
        <View style={[styles.bannerActions, { top: BANNER_HEIGHT + insets.top + spacing.sm }]}>
          <GlassButton
            title="Edit Profile"
            variant="secondary"
            size="sm"
            onPress={() => navigation.navigate('EditProfile')}
            style={styles.editBtn}
          />
        </View>
      </View>

      {/* Info */}
      <View style={styles.infoSection}>
        <Text style={styles.displayName} numberOfLines={1}>
          {friendlyDisplayName(user?.display_name, user?.username)}
        </Text>
        <Text style={styles.username} numberOfLines={1}>@{user?.username || 'user'}</Text>
        {user?.bio ? <Text style={styles.bio} numberOfLines={5}>{user.bio}</Text> : null}

        {/* Stats — inline, tappable, weight-driven hierarchy (skill §15). */}
        <View style={styles.statsRow}>
          <View style={styles.stat}>
            <Text style={styles.statNumber}>{formatCount(user?.posts_count || 0)}</Text>
            <Text style={styles.statLabel}>Posts</Text>
          </View>
          <View style={styles.statDivider} />
          <TouchableOpacity
            style={styles.stat}
            onPress={() => navigation.navigate('FollowList', { username: user?.username, mode: 'followers' })}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="View followers"
          >
            <Text style={styles.statNumber}>{formatCount(user?.followers_count || 0)}</Text>
            <Text style={styles.statLabel}>Followers</Text>
          </TouchableOpacity>
          <View style={styles.statDivider} />
          <TouchableOpacity
            style={styles.stat}
            onPress={() => navigation.navigate('FollowList', { username: user?.username, mode: 'following' })}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="View following"
          >
            <Text style={styles.statNumber}>{formatCount(user?.following_count || 0)}</Text>
            <Text style={styles.statLabel}>Following</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Profile Tabs — glass segmented pills (Posts · Media · Communities). */}
      <View style={styles.profileTabs}>
        {PROFILE_TABS.map((tab) => (
          <GlassPill
            key={tab.key}
            label={tab.label}
            icon={tab.icon}
            active={activeTab === tab.key}
            onPress={() => setActiveTab(tab.key)}
            accessibilityLabel={tab.label}
          />
        ))}
      </View>
    </View>
  );

  // The Media grid renders as its own list body (a single header + a wrapped grid
  // of tiles), so the FlatList only drives Posts / Communities rows.
  const renderMediaGrid = () => {
    if (posts.length === 0) {
      return (
        <View style={styles.emptyWrap}>
          <Asset3D name="emptyCluster" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.emptyText}>No media yet</Text>
          <Text style={styles.emptySubtext}>Your posts appear here as a gallery</Text>
          <TouchableOpacity
            style={styles.emptyCtaBtn}
            onPress={() => navigation.navigate('CreatePost')}
            activeOpacity={0.8}
          >
            <Ionicons name="add" size={16} color="#FFFFFF" />
            <Text style={styles.emptyCtaText}>Create Post</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={styles.mediaGrid}>
        {posts.map((p) => (
          <MediaTile
            key={p.id}
            post={p}
            size={mediaTileSize}
            onPress={() => navigation.navigate('Post', { id: p.id })}
          />
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Light lavender aurora → dark status-bar glyphs (skill §12). */}
      <StatusBar barStyle="dark-content" />
      <ParticleBackground theme="light" count={5} />

      <TopHeader />

      <ScreenTip
        floating
        tipKey="profile"
        title="Profile"
        text="Your profile, posts and communities. Edit your profile to stand out."
      />
      <FlatList
        data={activeTab === 'Media' ? [] : listData}
        keyExtractor={(item, index) => item?.id ?? String(index)}
        renderItem={({ item }) =>
          activeTab === 'Communities' ? (
            <TouchableOpacity
              style={styles.communityRow}
              onPress={() => navigation.navigate('Community', { name: item.name || item.id })}
              activeOpacity={0.8}
            >
              {item.icon ? (
                <CachedImage source={{ uri: item.icon }} style={styles.communityIcon} />
              ) : (
                <View style={[styles.communityIcon, styles.communityIconFallback]}>
                  <Text style={styles.communityIconLetter}>
                    {(item.displayName || item.name || '?').charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <View style={{ flex: 1 }}>
                <Text style={styles.communityName} numberOfLines={1}>{item.displayName || item.name}</Text>
                <Text style={styles.communityMeta} numberOfLines={1}>{(item.memberCount ?? item.membersCount ?? 0)} members</Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={colors.faint} />
            </TouchableOpacity>
          ) : (
            <ProfilePostCard post={item} onPress={() => navigation.navigate('Post', { id: item.id })} />
          )
        }
        ListHeaderComponent={ProfileHeader}
        ListEmptyComponent={
          activeTab === 'Media' ? (
            renderMediaGrid()
          ) : activeTab === 'Communities' ? (
            <View style={styles.emptyWrap}>
              <Asset3D name="community" size={120} style={{ marginBottom: spacing.sm }} />
              <Text style={styles.emptyText}>No communities yet</Text>
              <Text style={styles.emptySubtext}>Join communities to see them here</Text>
              <TouchableOpacity
                style={styles.emptyCtaBtn}
                onPress={() => navigation.navigate('Communities')}
                activeOpacity={0.8}
              >
                <Ionicons name="search" size={16} color="#FFFFFF" />
                <Text style={styles.emptyCtaText}>Discover Communities</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.emptyWrap}>
              <Asset3D name="emptyCluster" size={120} style={{ marginBottom: spacing.sm }} />
              <Text style={styles.emptyText}>No posts yet</Text>
              <Text style={styles.emptySubtext}>Share what's happening in crypto</Text>
              <TouchableOpacity
                style={styles.emptyCtaBtn}
                onPress={() => navigation.navigate('CreatePost')}
                activeOpacity={0.8}
              >
                <Ionicons name="add" size={16} color="#FFFFFF" />
                <Text style={styles.emptyCtaText}>Create Post</Text>
              </TouchableOpacity>
            </View>
          )
        }
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
          />
        }
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[styles.listContent, containerStyle]}
      />
    </View>
  );
}

const BANNER_HEIGHT = 148;
const AVATAR_SIZE = 88;
// Height of the floating header's icon row (below the safe-area inset). The list
// pads its top by insets.top + this so nothing scrolls behind the icons while
// the header itself stays transparent.
const TOP_HEADER_ROW = 48;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // Transparent so the full-bleed aurora shows through (skill §7).
    backgroundColor: 'transparent',
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // --- transparent floating top header ---
  // Absolutely positioned over the aurora/banner so there is NO opaque strip —
  // the client wants the top to read uniform/transparent. The list pads its top
  // by the same height (insets.top + TOP_HEADER_ROW) so content is never trapped
  // behind the icons; a soft scrim keeps the floating icons legible (skill §12).
  topHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 5,
  },
  topHeaderScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  topHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  topHeaderTitle: {
    ...typographyScale.h2,
    fontSize: 22,
    letterSpacing: -0.4,
    color: '#FFFFFF',
    ...fonts.bold,
    // Legible over the purple banner (and lighter content when scrolled).
    textShadowColor: 'rgba(0,0,0,0.28)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  topHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  topHeaderBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    // Glassy: frosted translucent pill with a hairline, floating over the banner.
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.40)',
  },

  bannerWrap: {
    position: 'relative',
    height: BANNER_HEIGHT + AVATAR_SIZE / 2,
  },
  banner: {
    width: '100%',
    height: BANNER_HEIGHT,
  },
  bannerScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: BANNER_HEIGHT - 56,
    height: 56,
  },
  bannerActions: {
    position: 'absolute',
    right: spacing.lg,
    top: BANNER_HEIGHT + spacing.sm,
  },
  editBtn: {
    minWidth: 118,
  },
  avatarWrap: {
    position: 'absolute',
    bottom: 0,
    left: spacing.lg,
    // Lift the avatar off the banner with the brand card shadow.
    ...shadowTokens.card,
    borderRadius: AVATAR_SIZE / 2,
    backgroundColor: colors.bg,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    borderWidth: 4,
    borderColor: colors.bg,
  },
  avatarFallback: {
    backgroundColor: colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    fontSize: 28,
    ...fonts.bold,
    color: colors.primary,
  },
  avatarEditBadge: {
    position: 'absolute',
    right: 2,
    bottom: 2,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.bg,
  },
  infoSection: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
  },
  displayName: {
    ...typographyScale.h2,
    fontSize: 24,
    // Negative tracking on large display text (skill §15).
    letterSpacing: -0.5,
    color: colors.ink,
    ...fonts.bold,
  },
  username: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.muted,
    marginTop: 1,
  },
  bio: {
    ...typographyScale.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink2,
    marginTop: spacing.sm,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.md,
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 5,
    paddingVertical: 2,
  },
  statDivider: {
    width: StyleSheet.hairlineWidth,
    height: 16,
    backgroundColor: colors.cardBorder,
    marginHorizontal: spacing.md,
  },
  statNumber: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    ...fonts.bold,
    ...tabularNums,
  },
  statLabel: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
  },

  profileTabs: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },

  // --- centered media gallery grid ---
  mediaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    gap: spacing.xs,
  },
  mediaTile: {
    borderRadius: glassTokens.radius.sm,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    ...shadowTokens.card,
  },
  mediaTileText: {
    ...typographyScale.caption,
    fontSize: 12,
    lineHeight: 15,
    ...fonts.semibold,
    color: '#FFFFFF',
    padding: spacing.sm,
    paddingBottom: 22,
  },
  mediaTileMeta: {
    position: 'absolute',
    left: spacing.sm,
    bottom: spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  mediaTileMetaText: {
    fontSize: 11,
    ...fonts.semibold,
    color: 'rgba(255,255,255,0.9)',
    ...tabularNums,
  },

  postCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  postCardContent: {
    padding: spacing.md,
  },
  postContent: {
    ...typographyScale.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink,
  },
  postTime: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    marginTop: spacing.xs,
  },
  postActions: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.cardBorder,
  },
  postAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  postActionCount: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    ...fonts.medium,
    ...tabularNums,
  },
  listContent: {
    // Clear the floating glass tab bar (A2) so the last row isn't hidden.
    paddingBottom: layoutTokens.bottomNavInset,
  },
  emptyWrap: {
    alignItems: 'center',
    paddingTop: 60,
    gap: spacing.sm,
  },
  emptyText: {
    ...typographyScale.h3,
    fontSize: 16,
    color: colors.ink,
  },
  emptySubtext: {
    ...typographyScale.body,
    fontSize: 13,
    color: colors.muted,
  },
  emptyCtaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.md,
    gap: spacing.xs,
    minHeight: 44,
    ...shadowTokens.bar,
  },
  emptyCtaText: {
    ...typographyScale.body,
    fontSize: 14,
    ...fonts.semibold,
    color: '#FFFFFF',
  },
  communityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  communityIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
  },
  communityIconFallback: {
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  communityIconLetter: {
    color: '#FFFFFF',
    fontSize: 18,
    ...fonts.bold,
  },
  communityName: {
    ...typographyScale.body,
    fontSize: 15,
    ...fonts.semibold,
    color: colors.ink,
  },
  communityMeta: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    marginTop: 2,
  },
});
